import { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Toolbar, Divider, Box, Paper } from '@mui/material';
import { DataGrid, GridToolbar, type GridColDef } from '@mui/x-data-grid';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { AxiosInstance } from 'axios';
import { getUsageMetric } from '@/actions/usage';
import type { UsageMetricResponse } from '@/types';

interface UserActivityGridProps {
  api: AxiosInstance;
  startDate: Date;
  endDate: Date;
}

interface ActivityDataItem {
  email: string;
  date: string;
  metric: string;
  value: number;
}

interface FlattenedActivityRow {
  id: string;
  email: string;
  date: string;
  metric: string;
  value: number;
}

function UserActivityGrid({ api, startDate, endDate }: UserActivityGridProps): React.JSX.Element {
  const [activityData, setActivityData] = useState<UsageMetricResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivityData = useCallback(async (): Promise<void> => {
    if (!api) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getUsageMetric(
        api,
        'user_activity',
        'count',
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
        [],
        {},
      );
      setActivityData(data);
    } catch (err) {
      console.error('Error fetching user activity data:', err);
      setError('Failed to load user activity data');
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate]);

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  // Process data to create flattened structure for DataGrid
  const flattenedData: FlattenedActivityRow[] = useMemo(() => {
    if (!activityData?.data) return [];

    const data = activityData.data as unknown as ActivityDataItem[];

    // Create a map to aggregate data by user, date, and metric
    const dataMap: Record<string, FlattenedActivityRow> = {};
    data.forEach((item) => {
      const key = `${item.email}_${item.date}_${item.metric}`;
      if (!dataMap[key]) {
        dataMap[key] = {
          id: key,
          email: item.email,
          date: item.date,
          metric: item.metric,
          value: 0,
        };
      }
      dataMap[key].value += typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0;
    });

    // Convert to array and format for display
    return Object.values(dataMap).map((item) => ({
      ...item,
      date: new Date(item.date).toLocaleDateString(),
      metric: item.metric
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      value: item.value || 0,
    }));
  }, [activityData]);

  const columns: GridColDef<FlattenedActivityRow>[] = [
    {
      field: 'email',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'metric',
      headerName: 'Activity Type',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'value',
      headerName: 'Count',
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<FlattenedActivityRow>) => (
        <span
          style={{
            fontWeight: params.value > 0 ? 'bold' : 'normal',
            color: params.value > 0 ? 'inherit' : 'text.secondary',
          }}
        >
          {params.value}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <>
        <Toolbar sx={{ minHeight: '55px !important' }}>
          <Typography variant='h6' sx={{ mr: 4 }}>
            User Activity by Day
          </Typography>
        </Toolbar>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography>Loading user activity data...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Toolbar sx={{ minHeight: '55px !important' }}>
          <Typography variant='h6' sx={{ mr: 4 }}>
            User Activity by Day
          </Typography>
        </Toolbar>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography color='error'>{error}</Typography>
        </Box>
      </>
    );
  }

  if (flattenedData.length === 0) {
    return (
      <>
        <Toolbar sx={{ minHeight: '55px !important' }}>
          <Typography variant='h6' sx={{ mr: 4 }}>
            User Activity by Day
          </Typography>
        </Toolbar>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            No user activity data available for the selected time range.
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Toolbar sx={{ minHeight: '55px !important' }}>
        <Typography variant='h6' sx={{ mr: 4 }}>
          User Activity by Day
        </Typography>
      </Toolbar>
      <Divider />

      <Box sx={{ p: 2 }}>
        <Paper sx={{ p: 2 }}>
          <DataGrid
            rows={flattenedData}
            columns={columns}
            getRowId={(row) => row.id}
            rowHeight={52}
            showToolbar
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
              sorting: {
                sortModel: [
                  { field: 'email', sort: 'asc' },
                  { field: 'date', sort: 'desc' },
                  { field: 'metric', sort: 'asc' },
                ],
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e0e0e0',
              },
              '& .MuiDataGrid-columnHeaders': {
                '& .MuiDataGrid-columnHeader': {
                  whiteSpace: 'pre-line',
                  lineHeight: '1.2',
                },
              },
              '& .MuiDataGrid-columnHeader': {
                whiteSpace: 'pre-line',
                lineHeight: '1.2',
              },
            }}
          />
        </Paper>
      </Box>
    </>
  );
}

export default UserActivityGrid;
